import React, { useCallback, useEffect, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import SearchableProjectDropdown from '../../../../components/SearchableProjectDropdown';
import type { ProjectOption } from '../../../../components/SearchableProjectDropdown/config';
import IconButton from '../../../../components/IconButton';
import { CloseIcon } from '../../../../components/IconButton/icons';
import { LEAVE_TYPE_OPTIONS, LeaveType } from '../../config';
import { describeOption, groupKeyForOption } from '../../timesheetDerive';
import './recent-projects-strip.scss';

interface RecentProjectsStripProps {
  batchId: number;
  /** Group keys already present in the week, so their chips are hidden. */
  presentKeys: Set<string>;
  /** Bump to refetch (after an entry is added). */
  refreshToken: number;
  /**
   * Offer a search for any other project. The week grid and the day view
   * carry their own add row with a search, so only the list needs it here.
   */
  showSearch?: boolean;
  onPickProject: (option: ProjectOption) => void;
  onPickLeave: (leaveType: LeaveType) => void;
}

/**
 * "Recent" chips: the projects and stages this person booked time to in the
 * last eight weeks that are not in this week yet, plus a search for anything
 * else and the three leave types. One click puts a row in the grid or starts
 * a Day view entry.
 */
const RecentProjectsStrip: React.FC<RecentProjectsStripProps> = ({
  batchId,
  presentKeys,
  refreshToken,
  showSearch = false,
  onPickProject,
  onPickLeave
}) => {
  const supabase = useSupabaseClient();
  const [recent, setRecent] = useState<ProjectOption[]>([]);
  const [searching, setSearching] = useState(false);

  const fetchRecent = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_recent_timesheet_projects', {
      p_exclude_batch_id: batchId,
      p_limit: 8
    });
    if (error) {
      console.error('Error fetching recent projects:', error);
      return;
    }
    setRecent((data || []) as ProjectOption[]);
  }, [supabase, batchId]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent, refreshToken]);

  const chips = recent.filter((o) => !presentKeys.has(groupKeyForOption(o)));
  const leaveChips = LEAVE_TYPE_OPTIONS.filter((o) => !presentKeys.has(`leave_${o.value}`));

  return (
    <div className="recent-strip">
      <span className="recent-strip__label">Recent</span>
      {chips.map((option) => {
        const { code, name, sub } = describeOption(option);
        return (
          <button
            key={`${option.entity_type}-${option.id}`}
            type="button"
            className="recent-chip"
            title={sub ? `${name} · ${sub}` : name}
            onClick={() => onPickProject(option)}
          >
            <span className="recent-chip__code">{code}</span>
            <span className="recent-chip__name">{name}</span>
          </button>
        );
      })}
      {leaveChips.map((o) => (
        <button
          key={o.value}
          type="button"
          className="recent-chip recent-chip--leave"
          onClick={() => onPickLeave(o.value)}
        >
          {o.label}
        </button>
      ))}
      {showSearch &&
        (searching ? (
          <span className="recent-strip__search form-ui">
            <SearchableProjectDropdown
              value={null}
              minSearchLength={1}
              onChange={(id, option) => {
                if (option) onPickProject(option);
                setSearching(false);
              }}
              placeholder="Search by job number or name…"
            />
            <IconButton icon={<CloseIcon size={14} />} label="Close search" size="sm" onClick={() => setSearching(false)} />
          </span>
        ) : (
          <button type="button" className="pill-btn pill-btn--sm pill-btn--ghost" onClick={() => setSearching(true)}>
            + Other project
          </button>
        ))}
    </div>
  );
};

export default RecentProjectsStrip;
